import { useState, useEffect, useRef, useMemo } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import type { PricingBreakdown, PricingRequest, PricingResponse } from '@carrierbridge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Calculator, Search } from 'lucide-react';
import { usePricingQuote } from '@/hooks/usePricing';
import { useCarriers } from '@/hooks/useCarriers';
import { usePeriodTags } from '@/hooks/usePeriodTags';
import { useSettings } from '@/hooks/useSettings';
import { useGroup } from '@/hooks/useGroup';
import { useAuthContext } from '@/contexts/AuthContext';
import { useOptionCatalog } from '@/hooks/useAncillaryRules';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { BreakdownDrawer } from '@/components/pricing/BreakdownDrawer';
import { useToast } from '@/components/ui/Toast';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { UNIT_OPTIONS } from '@/lib/geo-data';
import { apiErrorMessage } from '@/lib/api-errors';
import { quoteSchema, type QuoteFormValues, fallbackOptions } from './schema';
import { QuoteRouteSection } from './QuoteRouteSection';
import { QuoteDetailsSection } from './QuoteDetailsSection';
import { QuoteResultsPanel } from './QuoteResultsPanel';
import { GroupScopeBar, type QuoteScope } from './GroupScopeBar';
import { QuoteShipmentCriteria } from './QuoteShipmentCriteria';
import {
  EMPTY_SHIPMENT_CRITERIA,
  criteriaToRequest,
  type ShipmentCriteria,
} from './shipment-criteria';

/* ════════════════════════════════════════════════════════════════════
   QuotePage – Dense horizontal B2B layout
   ════════════════════════════════════════════════════════════════════ */

const QUOTE_STORAGE_KEY = 'carrierbridge:quote:last';

type RouteMode = 'direct' | 'from_site' | 'to_site';

type PersistedQuote = {
  originDepartment?: string;
  originPostalCode?: string;
  originCountry?: string;
  destDepartment?: string;
  destPostalCode?: string;
  destCountry?: string;
  quantity?: number;
  unit?: string;
  options?: string[];
  mode?: RouteMode;
};

/** Dernières saisies persistées après un calcul réussi (jamais la date).
 *  Toute erreur de lecture/parse est ignorée en silence. */
function loadPersistedQuote(): PersistedQuote | null {
  try {
    const raw = window.localStorage.getItem(QUOTE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as PersistedQuote;
  } catch {
    return null;
  }
}

function savePersistedQuote(value: PersistedQuote) {
  try {
    window.localStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage indisponible / quota : on ignore en silence.
  }
}

export function QuotePage() {
  const { t } = useTranslation();
  // Réhydratation des dernières saisies (une seule lecture au montage).
  const persisted = useMemo(() => loadPersistedQuote(), []);
  const [response, setResponse] = useState<PricingResponse | null>(null);
  const [selectedBreakdown, setSelectedBreakdown] = useState<PricingBreakdown | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [kgPerUnit, setKgPerUnit] = useState<number | ''>('');
  const [unitsPerPalette, setUnitsPerPalette] = useState<number | ''>('');
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [consigneeType, setConsigneeType] = useState('');
  const [shipmentCriteria, setShipmentCriteria] = useState<ShipmentCriteria>(EMPTY_SHIPMENT_CRITERIA);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() =>
    Array.isArray(persisted?.options)
      ? persisted!.options!.filter((o): o is string => typeof o === 'string')
      : [],
  );
  const [routeMode, setRouteMode] = useState<RouteMode>(() =>
    persisted?.mode === 'from_site' || persisted?.mode === 'to_site' ? persisted.mode : 'direct',
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Dernier calcul lancé (requête + saisies à persister) pour « Réessayer ».
  const lastQuoteRef = useRef<{ request: PricingRequest; persistPayload: PersistedQuote | null } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { toast } = useToast();
  const pricingMutation = usePricingQuote();
  const { data: periodTags = [] } = usePeriodTags();
  const { data: settings = {} } = useSettings();
  const { user } = useAuthContext();
  // Un corporate cote par défaut sur son groupe ; sans filiale, ni sélecteur ni changement.
  const { data: group } = useGroup();
  const groupSize = (group?.filiales.length ?? 0) + 1;
  const hasGroup = groupSize > 1;
  const [scope, setScope] = useState<QuoteScope>('group');
  const [hiddenOwners, setHiddenOwners] = useState<Record<string, boolean>>({});
  const isReadonly = user.role === 'readonly';
  const isSimplifiedView = isReadonly && settings.readonlyViewMode === 'simplified';
  const liveCatalog = useOptionCatalog();
  const { data: allCarriers = [] } = useCarriers();
  const logisticsSites = useMemo(
    () => allCarriers.filter((c) => c.carrierType === 'LOGISTICS' && c.siteDepartment),
    [allCarriers],
  );
  const logisticsSiteOptions = useMemo(
    () => logisticsSites.map((s) => ({ value: s.id, label: s.name })),
    [logisticsSites],
  );
  const selectedSite = useMemo(
    () => logisticsSites.find((s) => s.id === selectedSiteId) ?? null,
    [logisticsSites, selectedSiteId],
  );

  const defaultUnit = settings.defaultUnit || 'colis';

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteSchema(t)),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      originDepartment: persisted?.originDepartment || '',
      originPostalCode: persisted?.originPostalCode || '',
      originCountry: persisted?.originCountry || 'FRA',
      destDepartment: persisted?.destDepartment || '',
      destPostalCode: persisted?.destPostalCode || '',
      destCountry: persisted?.destCountry || 'FRA',
      quantity: typeof persisted?.quantity === 'number' && persisted.quantity >= 1 ? persisted.quantity : 1,
      unit: persisted?.unit || defaultUnit,
      // La date n'est jamais persistée : toujours aujourd'hui.
      date: new Date().toISOString().split('T')[0],
    },
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  // Apply settings defaults for unit / kgPerUnit / unitsPerPalette on first
  // load. Settings load async, so the form's initial defaultValues see an
  // empty object and fall back to 'colis' — without this effect the user's
  // configured default unit is silently ignored on every fresh mount.
  useEffect(() => {
    if (defaultsApplied || !settings) return;
    if (Object.keys(settings).length === 0) return;
    setDefaultsApplied(true);
    // L'unité restaurée depuis le storage prime sur le défaut des settings.
    if (settings.defaultUnit && settings.defaultUnit !== '' && !persisted?.unit) {
      setValue('unit', settings.defaultUnit);
    }
    if (settings.kgPerUnit && settings.kgPerUnit !== '') {
      setKgPerUnit(Number(settings.kgPerUnit));
    }
    if (settings.paletteUnitCount && settings.paletteUnitCount !== '') {
      setUnitsPerPalette(Number(settings.paletteUnitCount));
    }
  }, [settings, defaultsApplied, setValue, persisted]);

  const selectedUnit = watch('unit') || defaultUnit;
  const watchedQuantity = watch('quantity');

  // When picking a site for from_site / to_site routing, mirror its CP/dept
  // into the relevant form fields. The zod validator otherwise rejects the
  // submit silently because the visible site picker doesn't fill them in.
  useEffect(() => {
    if (routeMode === 'direct') return;
    if (!selectedSite) return;
    if (routeMode === 'from_site') {
      setValue('originDepartment', selectedSite.siteDepartment || '', { shouldValidate: true });
      setValue('originPostalCode', selectedSite.sitePostalCode || '', { shouldValidate: true });
    } else if (routeMode === 'to_site') {
      setValue('destDepartment', selectedSite.siteDepartment || '', { shouldValidate: true });
      setValue('destPostalCode', selectedSite.sitePostalCode || '', { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMode, selectedSite]);

  const computedWeightKg = kgPerUnit !== '' && watchedQuantity ? Number(kgPerUnit) * Number(watchedQuantity) : null;
  const computedPalettes = unitsPerPalette !== '' && watchedQuantity ? Number(watchedQuantity) / Number(unitsPerPalette) : null;

  // Requête qui détermine quelles grilles peuvent coter — la base des critères
  // proposés. Rendue en chaîne puis temporisée : l'objet change d'identité à
  // chaque rendu, la chaîne seulement quand une saisie compte vraiment, et la
  // temporisation évite un appel par frappe.
  const [wOriginDept, wOriginCp, wOriginCountry, wDestDept, wDestCp, wDestCountry, wDate] = watch([
    'originDepartment', 'originPostalCode', 'originCountry',
    'destDepartment', 'destPostalCode', 'destCountry', 'date',
  ]);
  const criteriaQuantity = Number(String(watchedQuantity ?? '').replace(',', '.'));
  const criteriaRouteReady =
    (!!wOriginDept || !!wOriginCp?.trim()) && (!!wDestDept || !!wDestCp?.trim()) && !!wDate && criteriaQuantity > 0
    && (routeMode === 'direct' || !!selectedSite);
  const criteriaRequestKey = JSON.stringify(
    criteriaRouteReady
      ? {
          // Vue groupe : les critères viennent aussi des grilles des filiales.
          ...(hasGroup && scope === 'group' ? { scope: 'group' as const } : {}),
          ...buildBaseRequest({
          originDepartment: wOriginDept,
          originPostalCode: wOriginCp,
          originCountry: wOriginCountry,
          destDepartment: wDestDept,
          destPostalCode: wDestCp,
          destCountry: wDestCountry,
          quantity: criteriaQuantity,
          unit: selectedUnit,
          date: wDate,
        }),
        }
      : null,
  );
  const debouncedCriteriaKey = useDebouncedValue(criteriaRequestKey, 400);
  const criteriaRequest = useMemo(
    () => JSON.parse(debouncedCriteriaKey) as PricingRequest | null,
    [debouncedCriteriaKey],
  );

  // Switch route mode, clearing whatever fields the previous mode wrote into
  // the form. Without this, switching from_site -> direct keeps the site's
  // postal code in originPostalCode, the backend still detects a logistics
  // context on submit and the result panel re-renders the "depuis 1 site" view.
  function selectMode(mode: RouteMode) {
    if (mode === routeMode) return;
    if (routeMode === 'from_site') {
      setValue('originDepartment', '', { shouldValidate: false });
      setValue('originPostalCode', '', { shouldValidate: false });
    } else if (routeMode === 'to_site') {
      setValue('destDepartment', '', { shouldValidate: false });
      setValue('destPostalCode', '', { shouldValidate: false });
    }
    // Clear the previous response so we don't leave a stale result panel while
    // the user fills the new mode's inputs.
    setResponse(null);
    setRouteMode(mode);
    setSelectedSiteId(null);
  }

  // Relance le calcul avec une requête donnée. Le succès persiste les saisies
  // dans localStorage ; l'échec est rendu inline par QuoteResultsPanel (l'état
  // d'erreur de la mutation est passé au panel), plus de toast ici.
  function runQuote(request: PricingRequest, persistPayload: PersistedQuote | null, quoteScope: QuoteScope = scope) {
    lastQuoteRef.current = { request, persistPayload };
    // Le périmètre part avec la requête : c'est le serveur qui décide des sociétés lues.
    const body: PricingRequest = hasGroup && quoteScope === 'group' ? { ...request, scope: 'group' } : request;
    pricingMutation.mutate(body, {
      onSuccess: (data) => {
        setResponse(data);
        if (persistPayload) savePersistedQuote(persistPayload);
      },
    });
  }

  function retryLastQuote() {
    if (!lastQuoteRef.current) return;
    runQuote(lastQuoteRef.current.request, lastQuoteRef.current.persistPayload);
  }

  /**
   * Ce qui détermine QUELLES grilles peuvent coter : trajet, date, quantité,
   * tags et conversions. Ni les critères de la marchandise (c'est ce qu'ils
   * servent à choisir), ni le destinataire ou les options (ils ne changent pas
   * la grille retenue).
   */
  function buildBaseRequest(values: QuoteFormValues): PricingRequest {
    return {
      originDepartment: routeMode === 'from_site' && selectedSite
        ? selectedSite.siteDepartment!
        : values.originDepartment,
      originPostalCode: routeMode === 'from_site' && selectedSite
        ? selectedSite.sitePostalCode || undefined
        : values.originPostalCode || undefined,
      originCountry: values.originCountry || 'FRA',
      destDepartment: routeMode === 'to_site' && selectedSite
        ? selectedSite.siteDepartment!
        : values.destDepartment,
      destPostalCode: routeMode === 'to_site' && selectedSite
        ? selectedSite.sitePostalCode || undefined
        : values.destPostalCode || undefined,
      destCountry: values.destCountry || 'FRA',
      quantity: values.quantity,
      unit: values.unit,
      date: values.date,
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      kgPerUnit: kgPerUnit !== '' ? Number(kgPerUnit) : undefined,
      unitsPerPalette: unitsPerPalette !== '' ? Number(unitsPerPalette) : undefined,
    };
  }

  function onSubmit(values: QuoteFormValues) {
    // Guardrail: when a site mode is active but no site is selected, surface
    // an error rather than letting the request fly with empty fields.
    if (routeMode !== 'direct' && !selectedSite) {
      toast('error', t('quote.pricingError'), t('quote.selectLogisticsSite'));
      return;
    }

    const request: PricingRequest = {
      ...buildBaseRequest(values),
      consigneeType: consigneeType ? (consigneeType as 'B2C' | 'B2B') : undefined,
      options: selectedOptions.length > 0 ? selectedOptions : undefined,
      ...criteriaToRequest(shipmentCriteria),
      // Don't pass carrierIds here: the logistics site is itself a Carrier
      // of type LOGISTICS and the pricing engine excludes those. Routing
      // through the site is detected automatically from originPostalCode.
    };

    // Saisies persistées après succès (valeurs du formulaire, jamais la date).
    const persistPayload: PersistedQuote = {
      originDepartment: values.originDepartment,
      originPostalCode: values.originPostalCode,
      originCountry: values.originCountry,
      destDepartment: values.destDepartment,
      destPostalCode: values.destPostalCode,
      destCountry: values.destCountry,
      quantity: values.quantity,
      unit: values.unit,
      options: selectedOptions,
      mode: routeMode,
    };

    runQuote(request, persistPayload);
  }

  // Champs rendus dans l'accordéon « Précisions » (QuoteDetailsSection). Si le
  // submit échoue en validation sur l'un d'eux alors que l'accordéon est replié,
  // l'erreur serait invisible : on l'ouvre d'office. (La date, elle, est en tête
  // de formulaire : toujours visible.)
  const DETAILS_FIELDS: (keyof QuoteFormValues)[] = ['originPostalCode', 'destPostalCode'];

  function onInvalid(validationErrors: FieldErrors<QuoteFormValues>) {
    if (DETAILS_FIELDS.some((field) => field in validationErrors)) {
      setDetailsOpen(true);
    }
  }

  function openBreakdown(breakdown: PricingBreakdown) {
    setSelectedBreakdown(breakdown);
    setDrawerOpen(true);
  }

  // Rebond depuis l'EmptyState « aucun transporteur » : ramène l'utilisateur
  // sur le premier champ interactif du formulaire.
  function focusFirstFormField() {
    formRef.current
      ?.querySelector<HTMLElement>('select, input, button:not([type="submit"])')
      ?.focus();
  }

  // Changer de périmètre relance le dernier calcul : ce qui est affiché est ce qui a été calculé.
  function changeScope(next: QuoteScope) {
    setScope(next);
    setHiddenOwners({});
    if (lastQuoteRef.current && response) runQuote(lastQuoteRef.current.request, null, next);
  }

  const ownerNames = new Map((response?.owners ?? []).map((o) => [o.companyId, o.name]));
  // Les sociétés décochées ne font que filtrer l'affichage (déjà trié par prix).
  const results = (response?.results ?? []).filter((r) => !r.ownerCompanyId || !hiddenOwners[r.ownerCompanyId]);
  const skippedCarriers = response?.skippedCarriers ?? [];
  const bestPrice = results.length > 0 ? results[0]?.totalPrice ?? null : null;
  const sourceLabelOf = (r: PricingBreakdown) =>
    r.ownerCompanyId && r.ownerCompanyId !== user.companyId ? ownerNames.get(r.ownerCompanyId) : undefined;

  const displayOptions: { value: string; label: string; group?: string | null; defaultEnabled?: boolean }[] =
    liveCatalog.length > 0
      ? liveCatalog.map((o) => ({ value: o.key, label: o.label, group: o.group, defaultEnabled: o.defaultEnabled }))
      : fallbackOptions(t);

  const prevCatalogKeysRef = useRef<string>('');
  // Quand des options ont été restaurées depuis le storage, elles priment sur
  // les options defaultEnabled du catalogue au premier chargement (sinon une
  // option décochée par l'utilisateur serait recochée à chaque montage).
  const skipCatalogDefaultsRef = useRef(Array.isArray(persisted?.options));
  useEffect(() => {
    if (liveCatalog.length === 0) return;
    const catalogKey = liveCatalog.map((o) => o.key).sort().join(',');
    if (catalogKey === prevCatalogKeysRef.current) return;
    prevCatalogKeysRef.current = catalogKey;
    if (skipCatalogDefaultsRef.current) {
      skipCatalogDefaultsRef.current = false;
      return;
    }
    const defaults = liveCatalog.filter((o) => o.defaultEnabled).map((o) => o.key);
    if (defaults.length > 0) {
      setSelectedOptions((prev) => [...new Set([...prev, ...defaults])]);
    }
  }, [liveCatalog]);

  const unitLabel = UNIT_OPTIONS.find((u) => u.value === selectedUnit)?.label || selectedUnit;

  return (
    <div className="flex flex-col lg:h-[calc(100vh-theme(spacing.6)*2-theme(spacing.14))]">
      <PageHeader title={t('quote.title')} icon={Calculator} className="shrink-0" />

      {/* Sous lg, les deux panneaux s'empilent et c'est la page qui défile ; à
          partir de lg on retrouve les deux colonnes à hauteur d'écran, chacune
          avec son propre défilement. */}
      <div className="flex flex-col gap-6 lg:min-h-0 lg:flex-1 lg:flex-row">
        {/* ── Left: Form panel ── */}
        <div className="flex w-full flex-col lg:min-h-0 lg:w-[520px] lg:flex-shrink-0 xl:w-[600px]">
          {/* La carte suit la hauteur de son contenu ; elle ne plafonne (et ne
              fait défiler le corps, bouton « Comparer » collé en bas) que si le
              contenu dépasse la hauteur disponible. */}
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            className="flex min-h-0 flex-col overflow-hidden rounded border border-border/80 bg-surface shadow-card"
          >
            {/* Form content */}
            <div className="min-h-0 flex-1 space-y-4 p-5 scrollbar-thin lg:overflow-y-auto">
              {/* ── Date du devis (défaut : aujourd'hui) — en tête, hors de « Précisions » ── */}
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <Input
                  label={t('quote.date')}
                  type="date"
                  error={errors.date?.message}
                  {...register('date')}
                />
              </div>

              <QuoteRouteSection
                t={t}
                form={form}
                routeMode={routeMode}
                onSelectMode={selectMode}
                logisticsSites={logisticsSites}
                logisticsSiteOptions={logisticsSiteOptions}
                selectedSite={selectedSite}
                selectedSiteId={selectedSiteId}
                setSelectedSiteId={setSelectedSiteId}
              />

              <div className="h-px bg-surface-subtle" />

              {/* ── Quantity / Unit (always visible) ── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Input
                  label={t('quote.quantity')}
                  type="number"
                  min={0}
                  step="any"
                  error={errors.quantity?.message}
                  {...register('quantity')}
                />
                <Select
                  label={t('quote.unit')}
                  options={UNIT_OPTIONS}
                  value={selectedUnit}
                  onChange={(e) => setValue('unit', e.target.value)}
                />
              </div>

              {/* ── Marchandise / flux : critères qui débloquent les grilles restreintes.
                  En vue, hors de l'accordéon : sans eux ces grilles ne cotent pas. ── */}
              <QuoteShipmentCriteria
                t={t}
                request={criteriaRequest}
                criteria={shipmentCriteria}
                onChange={setShipmentCriteria}
              />

              {/* ── Details accordion ── */}
              <div className="h-px bg-surface-subtle" />
              <QuoteDetailsSection
                t={t}
                open={detailsOpen}
                onToggle={() => setDetailsOpen(!detailsOpen)}
                form={form}
                routeMode={routeMode}
                kgPerUnit={kgPerUnit}
                setKgPerUnit={setKgPerUnit}
                unitsPerPalette={unitsPerPalette}
                setUnitsPerPalette={setUnitsPerPalette}
                unitLabel={unitLabel}
                computedWeightKg={computedWeightKg}
                computedPalettes={computedPalettes}
                periodTags={periodTags}
                selectedTagIds={selectedTagIds}
                setSelectedTagIds={setSelectedTagIds}
                consigneeType={consigneeType}
                setConsigneeType={setConsigneeType}
                displayOptions={displayOptions}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
              />
            </div>

            {/* ── CTA : reste visible en bas de carte quand le corps défile ── */}
            <div className="shrink-0 border-t border-border bg-surface px-5 py-3">
              <Button type="submit" className="w-full" loading={pricingMutation.isPending}>
                {pricingMutation.isPending ? (
                  t('quote.calculating')
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    {t('quote.compare')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* ── Right: Results ── */}
        <div className="min-w-0 flex-1 scrollbar-thin lg:overflow-y-auto">
          {hasGroup && (
            <GroupScopeBar
              scope={scope}
              onScope={changeScope}
              groupSize={groupSize}
              owners={response?.owners ?? []}
              ownCompanyId={user.companyId}
              hidden={hiddenOwners}
              onToggleOwner={(id) => setHiddenOwners((h) => ({ ...h, [id]: !h[id] }))}
            />
          )}
          <QuoteResultsPanel
            t={t}
            isPending={pricingMutation.isPending}
            response={response}
            results={results}
            skippedCarriers={skippedCarriers}
            bestPrice={bestPrice}
            unitLabel={unitLabel}
            isSimplifiedView={isSimplifiedView}
            onOpenBreakdown={openBreakdown}
            error={pricingMutation.isError
              ? (apiErrorMessage(pricingMutation.error, t('quote.pricingError')) ?? t('quote.pricingError'))
              : null}
            onRetry={retryLastQuote}
            onEditCriteria={focusFirstFormField}
            sourceLabelOf={sourceLabelOf}
          />
        </div>
      </div>

      <BreakdownDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        breakdown={selectedBreakdown}
      />
    </div>
  );
}
